"use client"

import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "../lib/i18n";
import logoUrl from "../transrentus-logo.png";
import { Sheet, SheetTrigger, SheetContent } from "./ui/sheet";
import { Button } from "./ui/button";
import { Menu } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "./ui/dropdown-menu";

export default function NavBar() {
  const { t, lang, setLang } = useTranslation();
  const location = useLocation();
  const [openMobile, setOpenMobile] = useState(false);

  const navItems = [
    { to: "/", key: "home" },
    { to: "/buses", key: "buses" },
    { to: "/microbuses", key: "microbuses" },
    { to: "/contact", key: "contact" },
  ];

  return (
    <nav className="sticky top-0 z-50 bg-white/60 dark:bg-slate-900/70 backdrop-blur-sm border-b">
      <div className="max-w-7xl mx-auto flex items-center justify-between py-3 px-4">
        <div className="flex items-center">
          <Link to="/">
            <img src={logoUrl} alt={t("logoAlt")} className="h-10 object-contain" />
          </Link>
        </div>

        <ul className="hidden md:flex gap-8 text-sm font-medium items-center">
          {navItems.map((item) => (
            <li key={item.to}>
              <Link
                to={item.to}
                className={`hover:text-sky-600 ${location.pathname === item.to ? "text-sky-700" : "text-slate-700"}`}
              >
                {t(item.key)}
              </Link>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="default" aria-label="Choose language">
                {lang === "en" ? "🇬🇧 EN" : "🇱🇹 LT"}
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end" className="w-36">
              <DropdownMenuRadioGroup
                value={lang}
                onValueChange={(v) => setLang(v as "en" | "lt")}
              >
                <DropdownMenuRadioItem value="en">🇬🇧 English</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="lt">🇱🇹 Lietuvių</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="md:hidden">
            <Sheet open={openMobile} onOpenChange={setOpenMobile}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu">
                  <Menu />
                </Button>
              </SheetTrigger>

              <SheetContent side="right" className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <Link to="/" onClick={() => setOpenMobile(false)}>
                    <img src={logoUrl} alt={t("logoAlt")} className="h-8 object-contain" />
                  </Link>
                </div>

                <nav>
                  <ul className="flex flex-col gap-4">
                    {navItems.map((item) => (
                      <li key={item.to}>
                        <Link
                          to={item.to}
                          onClick={() => setOpenMobile(false)}
                          className={`text-lg ${location.pathname === item.to ? "text-sky-700" : "text-slate-700"}`}
                        >
                          {t(item.key)}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>

                <div className="mt-6">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="default" aria-label="Choose language">
                        {lang === "en" ? "🇬🇧 EN" : "🇱🇹 LT"}
                      </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent className="w-48">
                      <DropdownMenuRadioGroup
                        value={lang}
                        onValueChange={(v) => {
                          setLang(v as "en" | "lt");
                          setOpenMobile(false);
                        }}
                      >
                        <DropdownMenuRadioItem value="en">🇬🇧 English</DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="lt">🇱🇹 Lietuvių</DropdownMenuRadioItem>
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
